import './mainPage.css';

import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaHeart, FaShoppingBag, FaStore } from 'react-icons/fa';
import { API_URL_PRODUCTS, BASE_URL } from '../../constants';
import AnnouncementBar from './AnnouncementBar';
import Footer from './Footer';
import Header from './Header';
import MainProducts from './MainProducts';
import { useCart } from './CartContext';
import { featuredProducts, navItems } from './data/mockData';

type PageResponse<T> = {
  content?: T[];
  message?: string;
};

type ApiProduct = {
  id: number;
  name: string;
  description?: string | null;
  price?: number | null;
  store?: StoreSummary | null;
  storeId?: number | null;
  store_id?: number | null;
  stores?: StoreSummary[];
};

type StoreSummary = {
  id?: number;
  name?: string;
  description?: string;
};

type ProductVariation = {
  id: number;
  variation?: {
    id?: number;
    name?: string;
  };
};

type ProductVariationValue = {
  id: number;
  productVariationId?: number;
  value?: string;
  name?: string;
};

type ProductVariationGroup = ProductVariation & {
  values: ProductVariationValue[];
};

type CombinationValue = {
  combinationId?: number;
  productVariationValueId?: number;
  productVariationValue?: ProductVariationValue;
};

type ProductCombination = {
  id: number;
  varCode?: string;
  combinationKey?: string;
  price?: number | null;
  available?: number | null;
  values?: CombinationValue[];
};

type ProductPhoto = {
  id: number;
  url?: string;
  altText?: string | null;
  primary?: boolean;
  position?: number;
};

type PhotoSetResponse = {
  valueIds?: number[];
  photos?: ProductPhoto[];
  message?: string;
};

const PRODUCT_VARIATIONS_URL = `${BASE_URL}/product-variations`;
const PRODUCT_VARIATION_VALUES_URL = `${BASE_URL}/product-variation-values`;
const PRODUCT_VARIATION_COMBINATIONS_URL = `${BASE_URL}/product-variation-combinations`;
const PRODUCT_VARIATION_COMBINATION_VALUES_URL = `${BASE_URL}/product-variation-combination-values`;
const PRODUCT_PHOTO_SETS_URL = `${BASE_URL}/product-variation-photo-sets`;

const fallbackImage = featuredProducts[0]?.image || '';

const currencyFormatter = new Intl.NumberFormat('es-CR', {
  style: 'currency',
  currency: 'CRC',
  maximumFractionDigits: 0,
});

const getPageItems = <T,>(data: PageResponse<T> | T[] | any): T[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const buildCombinationKey = (valueIds: number[]) =>
  [...valueIds].sort((a, b) => a - b).join('-');

const getCombinationValueId = (relation: CombinationValue) =>
  relation.productVariationValueId ?? relation.productVariationValue?.id;

const getCombinationValueIds = (combination: ProductCombination) =>
  (combination.values || [])
    .map(getCombinationValueId)
    .filter((valueId): valueId is number => !!valueId);

const getProductStore = (product: ApiProduct | null) => {
  if (!product) return { storeId: null, storeName: undefined };

  const embeddedStore = product.store || product.stores?.[0] || null;
  const storeId = embeddedStore?.id ?? product.storeId ?? product.store_id ?? null;
  const storeName = embeddedStore?.name || embeddedStore?.description || (storeId ? `Local ${storeId}` : undefined);

  return { storeId, storeName };
};

const getSelectedValueIds = (selectedValues: Record<number, number>) =>
  Object.values(selectedValues)
    .map(Number)
    .filter((valueId) => Number.isInteger(valueId) && valueId > 0);

const ProductDetails = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const productId = Number(id);
  const [searchTerm, setSearchTerm] = useState('');
  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [variations, setVariations] = useState<ProductVariationGroup[]>([]);
  const [combinations, setCombinations] = useState<ProductCombination[]>([]);
  const [selectedValues, setSelectedValues] = useState<Record<number, number>>({});
  const [photos, setPhotos] = useState<ProductPhoto[]>([]);
  const [activePhotoUrl, setActivePhotoUrl] = useState<string>(fallbackImage);
  const [addedToCart, setAddedToCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedValueIds = useMemo(() => getSelectedValueIds(selectedValues), [selectedValues]);
  const selectedCombinationKey = useMemo(() => buildCombinationKey(selectedValueIds), [selectedValueIds]);
  const requiredVariationIds = useMemo(
    () => variations.filter((variation) => variation.values.length > 0).map((variation) => variation.id),
    [variations]
  );
  const hasSelectedRequiredOptions = useMemo(
    () => requiredVariationIds.every((variationId) => !!selectedValues[variationId]),
    [requiredVariationIds, selectedValues]
  );

  const priceRange = useMemo(() => {
    const prices = combinations
      .map((combination) => Number(combination.price))
      .filter((price) => Number.isFinite(price) && price > 0);

    if (!prices.length) {
      const basePrice = Number(product?.price || 0);
      return { min: basePrice, max: basePrice };
    }

    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [combinations, product?.price]);

  const selectedCombination = useMemo(() => {
    if (!selectedCombinationKey) return null;
    return (
      combinations.find((combination) => {
        const ids = getCombinationValueIds(combination);
        return buildCombinationKey(ids) === selectedCombinationKey;
      }) || null
    );
  }, [combinations, selectedCombinationKey]);
  const inStockCombinations = useMemo(
    () => combinations.filter((combination) => Number(combination.available ?? 0) > 0),
    [combinations]
  );
  const isValueSelectable = useCallback(
    (variationId: number, valueId: number) => {
      if (!combinations.length) return true;

      const nextSelectedValues = {
        ...selectedValues,
        [variationId]: valueId,
      };
      const selectedValueIds = Object.values(nextSelectedValues)
        .map(Number)
        .filter((selectedValueId) => Number.isInteger(selectedValueId) && selectedValueId > 0);

      return inStockCombinations.some((combination) => {
        const combinationValueIds = getCombinationValueIds(combination);
        return selectedValueIds.every((selectedValueId) => combinationValueIds.includes(selectedValueId));
      });
    },
    [combinations.length, inStockCombinations, selectedValues]
  );

  const visiblePrice = selectedCombination?.price ?? priceRange.min;
  const visiblePriceLabel =
    selectedCombination || priceRange.min === priceRange.max
      ? currencyFormatter.format(Number(visiblePrice || 0))
      : `${currencyFormatter.format(priceRange.min)} - ${currencyFormatter.format(priceRange.max)}`;
  const productStore = useMemo(() => getProductStore(product), [product]);
  const productStoreLabel = productStore.storeName || (productStore.storeId ? `Local ${productStore.storeId}` : 'El Mercadito');
  const selectedOptions = useMemo(
    () =>
      variations
        .map((variation) => {
          const selectedValueId = selectedValues[variation.id];
          const selectedValue = variation.values.find((value) => value.id === selectedValueId);
          if (!selectedValue) return null;

          return `${variation.variation?.name || 'Opcion'}: ${selectedValue.value || selectedValue.name || selectedValue.id}`;
        })
        .filter((option): option is string => !!option),
    [selectedValues, variations]
  );
  const selectedAvailable = Number(selectedCombination?.available ?? 0);
  const canAddToCart = !!product && hasSelectedRequiredOptions && !!selectedCombination && selectedAvailable > 0;

  const fetchVariationValues = async (productVariationId: number) => {
    const response = await axios.get<PageResponse<ProductVariationValue>>(PRODUCT_VARIATION_VALUES_URL, {
      params: { productVariationId, page: 0, size: 100 },
    });
    return getPageItems<ProductVariationValue>(response.data);
  };

  const handleAddToCart = () => {
    if (!canAddToCart || !product || !selectedCombination) return;

    addItem({
      productId: product.id,
      combinationId: selectedCombination.id,
      name: product.name,
      price: Number(visiblePrice || 0),
      image: activePhotoUrl || fallbackImage,
      quantity: 1,
      available: selectedCombination.available,
      options: selectedOptions,
      code: selectedCombination.varCode || selectedCombination.combinationKey,
      storeId: productStore.storeId,
      storeName: productStoreLabel,
    });
    setAddedToCart(true);
    window.setTimeout(() => setAddedToCart(false), 1400);
  };

  const fetchCombinationValues = async (combinationId: number) => {
    const response = await axios.get<PageResponse<CombinationValue>>(PRODUCT_VARIATION_COMBINATION_VALUES_URL, {
      params: { combinationId, page: 0, size: 100 },
    });
    return getPageItems<CombinationValue>(response.data);
  };

  const resolvePhotos = useCallback(async (valueIds: number[]) => {
    if (!productId || !valueIds.length) {
      setPhotos([]);
      setActivePhotoUrl(fallbackImage);
      return;
    }

    const params = new URLSearchParams();
    params.append('productId', String(productId));
    valueIds.forEach((valueId) => params.append('valueIds', String(valueId)));

    const response = await axios.get<PhotoSetResponse>(`${PRODUCT_PHOTO_SETS_URL}/resolve?${params.toString()}`);
    const nextPhotos = (response.data.photos || []).sort(
      (a, b) => Number(a.position || 0) - Number(b.position || 0) || a.id - b.id
    );
    setPhotos(nextPhotos);
    setActivePhotoUrl(nextPhotos.find((photo) => photo.primary)?.url || nextPhotos[0]?.url || fallbackImage);
  }, [productId]);

  useEffect(() => {
    const loadProduct = async () => {
      if (!productId) {
        setError('Producto no encontrado.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [productResponse, variationsResponse, combinationsResponse] = await Promise.all([
          axios.get<ApiProduct>(`${API_URL_PRODUCTS}/${productId}`),
          axios.get<PageResponse<ProductVariation>>(PRODUCT_VARIATIONS_URL, {
            params: { productId, page: 0, size: 50 },
          }),
          axios.get<PageResponse<ProductCombination>>(PRODUCT_VARIATION_COMBINATIONS_URL, {
            params: { productId, page: 0, size: 200 },
          }),
        ]);

        const variationGroups = await Promise.all(
          getPageItems<ProductVariation>(variationsResponse.data).map(async (variation) => ({
            ...variation,
            values: await fetchVariationValues(variation.id).catch(() => []),
          }))
        );

        const combinationItems = await Promise.all(
          getPageItems<ProductCombination>(combinationsResponse.data).map(async (combination) => ({
            ...combination,
            values: await fetchCombinationValues(combination.id).catch(() => []),
          }))
        );

        setProduct(productResponse.data);
        setVariations(variationGroups);
        setCombinations(combinationItems);
        setSelectedValues({});
        setPhotos([]);
        setActivePhotoUrl(fallbackImage);
      } catch {
        setError('No se pudo cargar el detalle del producto.');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, resolvePhotos]);

  useEffect(() => {
    if (selectedValueIds.length) {
      resolvePhotos(selectedValueIds).catch(() => {
        setPhotos([]);
        setActivePhotoUrl(fallbackImage);
      });
    }
  }, [resolvePhotos, selectedValueIds]);

  return (
    <div className="app-shell">
      <AnnouncementBar />
      <Header navItems={navItems} searchValue={searchTerm} onSearchChange={setSearchTerm} />

      {searchTerm.trim() ? (
        <MainProducts searchTerm={searchTerm} onClearSearch={() => setSearchTerm('')} />
      ) : (
        <main className="section container product-details-view">
          <Link to="/" className="product-details-back">
            <FaArrowLeft /> Volver a la tienda
          </Link>

          {loading ? (
            <div className="main-products-state">Cargando detalle...</div>
          ) : error || !product ? (
            <div className="main-products-state main-products-state--error">{error || 'Producto no encontrado.'}</div>
          ) : (
            <section className="product-details-grid">
              <div className="product-details-gallery">
                <div className="product-details-main-image">
                  <img src={activePhotoUrl || fallbackImage} alt={product.name} />
                </div>
                {photos.length > 1 ? (
                  <div className="product-details-thumbs">
                    {photos.map((photo) => (
                      <button
                        type="button"
                        key={photo.id}
                        className={photo.url === activePhotoUrl ? 'active' : ''}
                        onClick={() => setActivePhotoUrl(photo.url || fallbackImage)}
                      >
                        <img src={photo.url || fallbackImage} alt={photo.altText || product.name} />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="product-details-content">
                <span className="product-details-kicker">Detalle de producto</span>
                <h1>{product.name}</h1>
                <div className="product-details-store">
                  <FaStore /> {productStoreLabel}
                </div>
                <p>{product.description || 'Producto disponible en El Mercadito.'}</p>

                <div className="product-details-price">{visiblePriceLabel}</div>
                <div className="product-details-stock">
                  Disponible: {selectedCombination ? selectedCombination.available ?? 0 : 'Seleccione las opciones'}
                </div>

                <div className="product-details-options">
                  {variations.map((variation) => (
                    <div key={variation.id} className="product-details-option-group">
                      <h3>{variation.variation?.name || `Variacion #${variation.id}`}</h3>
                      <div className="product-details-option-values">
                        {variation.values.map((value) => {
                          const label = value.value || value.name || `Valor #${value.id}`;
                          const active = selectedValues[variation.id] === value.id;
                          const disabled = !isValueSelectable(variation.id, value.id);
                          return (
                            <button
                              type="button"
                              key={value.id}
                              className={active ? 'active' : ''}
                              disabled={disabled}
                              title={disabled ? 'Sin inventario disponible' : undefined}
                              onClick={() =>
                                setSelectedValues((current) => ({
                                  ...current,
                                  [variation.id]: value.id,
                                }))
                              }
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {!hasSelectedRequiredOptions ? (
                  <div className="product-details-code product-details-code--warning">
                    Seleccione todas las opciones antes de agregar al carrito.
                  </div>
                ) : selectedCombination ? (
                  <div className="product-details-code">Codigo: {selectedCombination.varCode || selectedCombination.combinationKey || '-'}</div>
                ) : (
                  <div className="product-details-code product-details-code--warning">
                    No hay inventario configurado para esta combinacion.
                  </div>
                )}

                <div className="product-details-actions">
                  <button type="button" className="add-cart-button" disabled={!canAddToCart} onClick={handleAddToCart}>
                    <FaShoppingBag /> {addedToCart ? 'Agregado' : 'Agregar al carrito'}
                  </button>
                  <button type="button" className="product-details-favorite">
                    <FaHeart /> Favorito
                  </button>
                </div>
              </div>
            </section>
          )}
        </main>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetails;
